/**
 * Creative Performance — the shared view model.
 *
 * One model, every platform, both apps. It turns the governed projection from
 * GET /api/creative-assets/creative-performance into exactly what a panel needs
 * to render, and it is pure: no fetch, no storage, no router, no clock beyond a
 * reference time a caller passes in. That is what lets Cortex and Quantum prove
 * they present the same creative the same way from the same payload.
 *
 * The single rule that shapes all of it: a metric is either a reading the
 * connector reported or it is absent, and absent never renders as 0. Every
 * formatter here takes the metric's declared availability alongside its value,
 * so no caller can format a value without also deciding what its absence means.
 */

/*
 * Availability states, mirrored from the backend projection. Presentation
 * copy lives here rather than in a component so the two apps cannot drift into
 * describing the same governed state differently.
 */
export const METRIC_AVAILABILITY = Object.freeze({
  REPORTED: "reported",
  PARTIAL: "partial",
  UNAVAILABLE: "unavailable",
  NOT_REPORTED_BY_SOURCE: "not_reported_by_source",
});

export const CREATIVE_PERFORMANCE_STATE = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  REPORTING: "reporting",
  NO_OBSERVATIONS: "no_observations",
  PLATFORM_UNSUPPORTED: "platform_unsupported",
  WINDOW_UNAVAILABLE: "window_unavailable",
  CONNECTOR_UNAVAILABLE: "connector_unavailable",
  CONNECTOR_NOT_CONNECTED: "connector_not_connected",
  AUTHORIZATION_INSUFFICIENT: "authorization_insufficient",
  REPORTING_PREPARING: "reporting_preparing",
  ERROR: "error",
});

/*
 * Every state gets its own title and body. Collapsing them into a generic
 * "no data" would tell a viewer that a creative underperformed when in fact
 * the connector was never asked, or the platform reports no creative data at
 * all. The distinction is the product.
 */
const STATE_COPY = Object.freeze({
  [CREATIVE_PERFORMANCE_STATE.LOADING]: {
    title: "Loading creative reporting…",
    body: "Resolving which creatives produced this platform's delivery.",
  },
  /*
   * The source answered; nothing delivered. That is a fact about the window,
   * not a fault, and it must never borrow the vocabulary of one.
   */
  [CREATIVE_PERFORMANCE_STATE.NO_OBSERVATIONS]: {
    title: "No creative performance data",
    body: "No creative performance data is available for the selected timeframe. Widening the window may surface earlier delivery; nothing is estimated in the meantime.",
  },
  [CREATIVE_PERFORMANCE_STATE.PLATFORM_UNSUPPORTED]: {
    title: "Creative reporting unavailable for this platform",
    body: "This platform's connector reports performance at the campaign level only. Creative-level results are not withheld here — they are not reported by the source.",
  },
  [CREATIVE_PERFORMANCE_STATE.WINDOW_UNAVAILABLE]: {
    title: "No reporting window resolved",
    body: "No approved flight was available to scope creative reporting for this platform.",
  },
  [CREATIVE_PERFORMANCE_STATE.CONNECTOR_UNAVAILABLE]: {
    title: "Creative reporting is temporarily unavailable",
    body: "The reporting source could not be reached for this window. No values are shown when delivery cannot be verified.",
  },
  /*
   * These three are not failures, and saying "temporarily unavailable" would
   * send someone to retry when the real answer is to connect an account, widen
   * an authorization, or wait for the source to finish.
   */
  [CREATIVE_PERFORMANCE_STATE.CONNECTOR_NOT_CONNECTED]: {
    title: "No creative reporting source is connected",
    body: "This advertiser has no linked reporting account for this platform, so creative-level delivery has never been collected. Connecting one starts collection; it does not backfill.",
  },
  [CREATIVE_PERFORMANCE_STATE.AUTHORIZATION_INSUFFICIENT]: {
    title: "Creative reporting is not authorized",
    body: "The connected account is missing the permission needed to read creative-level delivery. The data exists at the source and is not being shown here rather than not existing.",
  },
  [CREATIVE_PERFORMANCE_STATE.REPORTING_PREPARING]: {
    title: "Creative reporting is still being prepared",
    body: "The source is assembling this window's creative delivery. Results appear once it finishes — nothing is estimated in the meantime.",
  },
  [CREATIVE_PERFORMANCE_STATE.ERROR]: {
    title: "Creative reporting failed to load",
    body: "Try again shortly. Nothing is estimated in place of reporting that did not arrive.",
  },
  [CREATIVE_PERFORMANCE_STATE.IDLE]: {
    title: "Choose an advertiser",
    body: "Creative performance resolves once an advertiser is selected.",
  },
});

const currencyWhole = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const currencyPrecise = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integers = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

export function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

/*
 * The em dash is the only thing an unavailable metric may render as. It reads
 * as "no reading", where 0 reads as "no performance" — and the second is a
 * claim the data does not support.
 */
export const UNAVAILABLE_DISPLAY = "—";

export function formatMetricValue(value, unit, { compact: useCompact = false } = {}) {
  if (!isFiniteNumber(value)) return UNAVAILABLE_DISPLAY;
  switch (unit) {
    case "currency":
      if (useCompact && Math.abs(value) >= 10000) return `$${compact.format(value)}`;
      /*
       * A cost the source reported is never printed as "$0.00". Sub-cent costs
       * exist — a completed view can be worth a fraction of a cent — and at two
       * decimals that rounds to a figure claiming the delivery was free. "<$0.01"
       * says small, which is what the counts support; a genuine zero still
       * prints as zero below, because that is a reading.
       */
      if (value > 0 && value < 0.005) return "<$0.01";
      if (value < 0 && value > -0.005) return ">-$0.01";
      return Math.abs(value) < 100 && value !== 0
        ? currencyPrecise.format(value)
        : currencyWhole.format(value);
    case "percent":
      return `${value.toFixed(Math.abs(value) < 10 ? 2 : 1)}%`;
    case "count":
      return useCompact && Math.abs(value) >= 10000
        ? compact.format(value)
        : integers.format(value);
    default:
      return String(value);
  }
}

/*
 * Why a metric is absent, in words a client can act on. Returning null for a
 * reported metric keeps callers from decorating values that need no
 * explanation.
 */
export function describeAvailability(availability, metricLabel = "This metric") {
  switch (availability) {
    case METRIC_AVAILABILITY.PARTIAL:
      return `${metricLabel} is a partial total — some delivery rows for this creative did not report it.`;
    case METRIC_AVAILABILITY.UNAVAILABLE:
      return `${metricLabel} was not reported for this creative in this window.`;
    case METRIC_AVAILABILITY.NOT_REPORTED_BY_SOURCE:
      return `${metricLabel} is not reported by this platform's source.`;
    default:
      return null;
  }
}

export function buildMetricCell(creative, metric) {
  const availability = creative?.metricAvailability?.[metric.key] || METRIC_AVAILABILITY.UNAVAILABLE;
  const value = creative?.metrics?.[metric.key];
  const isAvailable = availability === METRIC_AVAILABILITY.REPORTED ||
    availability === METRIC_AVAILABILITY.PARTIAL;

  return {
    key: metric.key,
    label: metric.label,
    unit: metric.unit,
    value: isAvailable ? value : null,
    display: isAvailable ? formatMetricValue(value, metric.unit, { compact: true }) : UNAVAILABLE_DISPLAY,
    availability,
    note: describeAvailability(availability, metric.label),
    isAvailable,
    isPartial: availability === METRIC_AVAILABILITY.PARTIAL,
  };
}

/*
 * Format describes what the creative IS; the delivered size describes what the
 * platform served. Both are shown when known, and neither is guessed from the
 * other, because a 16:9 source can be served into a placement that is not.
 */
export function describeCreativeShape(creative) {
  const parts = [];
  const format = String(creative?.format || "").trim();
  if (format) parts.push(format.charAt(0).toUpperCase() + format.slice(1));
  if (creative?.aspectRatio) parts.push(creative.aspectRatio);
  const sizes = Array.isArray(creative?.deliveredSizes) ? creative.deliveredSizes : [];
  if (sizes.length === 1) parts.push(sizes[0]);
  else if (sizes.length > 1) parts.push(`${sizes.length} sizes`);
  return parts.join(" · ") || null;
}

const APPROVAL_LABELS = Object.freeze({
  approved: "Approved",
  partially_approved: "Partially approved",
  draft: "Draft",
});

export function describeApprovalState(creative) {
  const state = String(creative?.approvalState || "").trim().toLowerCase();
  if (!state) return null;
  return APPROVAL_LABELS[state] || state.charAt(0).toUpperCase() + state.slice(1).replace(/_/g, " ");
}

/**
 * Turn the governed projection into the panel's view model.
 *
 * `status` lets a caller declare a state the payload cannot describe — loading
 * before the first response, error when the request failed, idle before an
 * advertiser exists — without inventing a payload to carry it.
 */
export function buildCreativePerformanceViewModel({
  projection = null,
  status = null,
  platformLabel = "",
  window: requestedWindow = null,
} = {}) {
  const declaredState =
    status && status !== CREATIVE_PERFORMANCE_STATE.REPORTING ? status : projection?.state || null;

  const state = declaredState || CREATIVE_PERFORMANCE_STATE.IDLE;
  const label = projection?.platformLabel || platformLabel || "this platform";

  const metricsByKey = new Map(
    (projection?.sortOptions || []).map((metric) => [metric.key, metric])
  );

  const columns = (projection?.supportedMetrics || [])
    .map((key) => metricsByKey.get(key))
    .filter(Boolean);

  const creatives = (projection?.creatives || []).map((creative) => ({
    ...creative,
    shape: describeCreativeShape(creative),
    approvalLabel: describeApprovalState(creative),
    cells: columns.map((metric) => buildMetricCell(creative, metric)),
  }));

  const copy = STATE_COPY[state] || null;
  const isReporting = state === CREATIVE_PERFORMANCE_STATE.REPORTING && creatives.length > 0;

  return {
    state,
    isReporting,
    platformLabel: label,
    title: "Creative Performance",
    /*
     * Names the platform in the subtitle so the section's scope is legible on
     * its own, without the reader having to remember which page they are on.
     */
    subtitle: `Which ${label} creatives produced this platform's delivery`,
    columns,
    creatives,
    sort: projection?.sort || null,
    sortOptions: projection?.sortOptions || [],
    window: projection?.window || requestedWindow || null,
    source: projection?.source || null,
    reconciliation: projection?.reconciliation || null,
    /*
     * The projection's own reason is preferred over generic copy whenever it
     * exists — it names the actual governed cause.
     */
    stateTitle: copy?.title || null,
    stateBody: projection?.reason || copy?.body || null,
  };
}

export function describeWindow(window) {
  if (!window?.startDate || !window?.endDate) return null;
  return `${window.startDate} → ${window.endDate}`;
}
